import { InboxOutlined } from '@ant-design/icons'
import { Upload } from 'antd'
import type { UploadChangeParam, UploadFile } from 'antd/es/upload'
import { validateFile } from 'features/files/utils/fileUploadUtils'
import { type FC, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

const { Dragger } = Upload

interface FileUploadDraggerProps {
  fileList: ReadonlyArray<UploadFile>
  onFileListChange: (fileList: ReadonlyArray<UploadFile>) => void
  maxFileSize?: number
  acceptedFileTypes?: ReadonlyArray<string>
  multiple?: boolean
  disabled?: boolean
  className?: string
  condensed?: boolean
}

const StyledDragger = styled(Dragger)<{ $condensed: boolean }>`
  .ant-upload-drag {
    border: 0.125rem dashed ${CantileverTheme.colors.grayscale[300]};
    border-radius: 0.5rem;
    background: ${CantileverTheme.colors.grayscale[100]};
    padding: ${({ $condensed }) => ($condensed ? '0rem' : '2rem 1rem')};
    text-align: center;
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
      border-color: ${CantileverTheme.colors.grayscale[400]};
      background: ${CantileverTheme.colors.grayscale[200]};
    }

    &.ant-upload-drag-hover {
      border-color: ${CantileverTheme.colors.grayscale[400]};
      background: ${CantileverTheme.colors.grayscale[200]};
    }
  }

  .ant-upload-drag-container {
    padding: 0;
  }
`

const UploadIcon = styled.div<{ $condensed: boolean }>`
  font-size: 3rem;
  color: ${CantileverTheme.colors.grayscale[500]};
  margin-bottom: ${({ $condensed }) => ($condensed ? '0rem' : '1rem')};
`

const UploadText = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${CantileverTheme.colors.grayscale[800]};
  margin-bottom: 0.5rem;
`

const UploadHint = styled.div`
  font-size: 0.875rem;
  color: ${CantileverTheme.colors.grayscale[500]};
`

/**
 * A dragger component for file uploads that handles file selection and validation
 */
export const FileUploadDragger: FC<FileUploadDraggerProps> = ({
  fileList,
  onFileListChange,
  maxFileSize = 20,
  acceptedFileTypes = ['*/*'],
  multiple = true,
  disabled,
  condensed = false,
}) => {
  const handleChange = useCallback(
    (info: UploadChangeParam<UploadFile>) => {
      onFileListChange(info.fileList)
    },
    [onFileListChange]
  )

  const beforeUpload = useCallback(
    (file: File) => validateFile(file, maxFileSize, acceptedFileTypes),
    [maxFileSize, acceptedFileTypes]
  )

  const uploadProps = useMemo(
    () => ({
      name: 'file',
      multiple,
      fileList,
      beforeUpload,
      onChange: handleChange,
      disabled,
      onRemove: (file: UploadFile) => {
        const newFileList = fileList.filter((f) => f.uid !== file.uid)
        onFileListChange(newFileList)
        return true
      },
    }),
    [multiple, fileList, beforeUpload, handleChange, disabled, onFileListChange]
  )

  return (
    <StyledDragger
      {...uploadProps}
      fileList={[...fileList]}
      $condensed={condensed}>
      <UploadIcon $condensed={condensed}>
        <InboxOutlined />
      </UploadIcon>
      <UploadText>Click or drag files to this area to upload</UploadText>
      <UploadHint>
        {acceptedFileTypes.includes('*/*')
          ? `Supports all file types. Max ${maxFileSize}MB per file.`
          : `Accepted types: ${acceptedFileTypes.join(', ')}. Max ${maxFileSize}MB per file.`}
      </UploadHint>
    </StyledDragger>
  )
}
